import { Observable, BehaviorSubject, Subject, from } from "rxjs";
import { Inject, Injectable } from "@angular/core";
import { IHydraClient } from "heracles/IHydraClient";
import { IApiDocumentation } from "heracles/DataModel/IApiDocumentation";
import { IWebResource } from "heracles/DataModel/IWebResource";
import { IClass } from "heracles/DataModel/IClass";

@Injectable({ providedIn: "root" })
export class DiscoveryService {
  private readonly _documentation: Subject<IApiDocumentation>;
  private readonly _resource: Subject<IWebResource>;
  private readonly _selectedClass: Subject<IClass>;
  private readonly _selectedResource: Subject<IWebResource>;

  public readonly documentation: Observable<IApiDocumentation>;
  public readonly resource: Observable<IWebResource>;
  public readonly selectedClass: Observable<IClass>;
  public readonly selectedResource: Observable<IWebResource>;

  public isSelectedClassAvailable = false;
  public isResourceAvailable = false;

  constructor(@Inject("HydraClient") private hydraClient: IHydraClient) {
    this._documentation = new BehaviorSubject<IApiDocumentation>(null);
    this.documentation = this._documentation.asObservable();
    this._resource = new BehaviorSubject<IWebResource>(null);
    this.resource = this._resource.asObservable();
    this._selectedClass = new BehaviorSubject<IClass>(null);
    this.selectedClass = this._selectedClass.asObservable();
    this._selectedResource = new BehaviorSubject<IWebResource>(null);
    this.selectedResource = this._selectedResource.asObservable();
  }

  public selectClassOf($class: IClass): void {
    this.isSelectedClassAvailable = true;
    this._selectedClass.next($class);
  }

  public load(resource: IWebResource): void {
    this.isResourceAvailable = false;
    if (!!resource) {
      this.isResourceAvailable = true;
      from(this.hydraClient.getResource(resource))
        .subscribe(_ => this._selectedResource.next(_));
    }
  }

  public loadApiDocumentation(url: string): void {
    if (!!url && url.toLowerCase().indexOf("http") !== -1) {
      this._resource.next(null);
      this.isSelectedClassAvailable = false;
      this._selectedClass.next(null);
      this.isResourceAvailable = false;
      this._selectedResource.next(null);
      from(this.hydraClient.getApiDocumentation(url)).subscribe(_ => this.onDocumentationLoaded(_, url));
    }
  }

  public makeLabelFor(resource: any): string {
    let result = resource.displayName;
    if (!result && !!resource.iri && resource.iri.length > 0 && resource.iri.charAt(0) !== "_") {
      result = resource.iri;
      let position = result.indexOf("?");
      if (position !== -1) {
        result = result.substring(0, position);
      }

      position = result.indexOf("#");
      if (position !== -1) {
        result = result.substring(position + 1);
      }

      position = result.lastIndexOf("/");
      if (position !== -1) {
        result = result.substring(position + 1);
      }
    }

    return result;
  }

  private onDocumentationLoaded(documentation: IApiDocumentation, url: string): void {
    this._documentation.next(documentation);
    if (!!documentation.entryPoint) {
      from(documentation.getEntryPoint()).subscribe(_ => this._resource.next(_));
    } else {
      from(this.hydraClient.getResource(url)).subscribe(_ => this._resource.next(_));
    }
  }
}
